import { Box } from '@/components/ui/box';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/utils/classUtils';
import { Copy } from 'lucide-react';
import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  type CSSProperties,
  type FC,
  type ReactElement,
  type ReactNode,
} from 'react';
import ReactMarkdown, { type Options } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';

/** Regulatory Watch update detail body: paragraph small/regular — text-sm, ~144% line height, 0.5% letter spacing */
const UPDATE_DETAIL_BODY =
  'text-sm font-normal leading-[1.44] tracking-[0.005em] text-neutral-800';
const UPDATE_DETAIL_OVERLINE =
  'text-[11px] font-medium leading-[1.44] tracking-[0.005em] text-neutral-800';

/** Newsfeed card preview: paragraph mini/regular only — no bold (DITTO-1260) */
const UPDATE_CARD_PREVIEW_BODY =
  'text-xs font-normal leading-[1.6] tracking-[0.015em] text-neutral-500';

function isAllCapsToken(word: string): boolean {
  const letters = word.replace(/[^A-Za-z]/g, '');
  if (letters.length < 2) return false;
  return letters === letters.toUpperCase();
}

function splitCapsInString(text: string): ReactNode {
  const parts = text.split(/(\s+)/);
  const out: ReactNode[] = [];
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (!part) continue;
    if (/^\s+$/.test(part)) {
      out.push(part);
      continue;
    }
    if (isAllCapsToken(part)) {
      out.push(
        <span key={i} className={UPDATE_DETAIL_OVERLINE}>
          {part}
        </span>
      );
    } else {
      out.push(part);
    }
  }
  if (out.length === 0) return null;
  if (out.length === 1) return out[0];
  return <>{out}</>;
}

/** Maps text nodes to overline for all-caps tokens; no bold in body (DITTO-1321) */
function mapTextForUpdateDetail(node: ReactNode): ReactNode {
  return Children.map(node, (child, i) => {
    if (child == null || typeof child === 'boolean') return child;
    if (typeof child === 'string' || typeof child === 'number') {
      return <Fragment key={i}>{splitCapsInString(String(child))}</Fragment>;
    }
    if (isValidElement(child)) {
      const el = child as ReactElement<{ children?: ReactNode }>;
      return cloneElement(el, {
        key: child.key ?? i,
        children: mapTextForUpdateDetail(el.props.children),
      });
    }
    return child;
  });
}

export type MarkdownTextProps = Options & {
  tableCopy?: boolean;
  className?: string;
  size?: 'base' | 'sm';
  /** Typography for Regulatory Watch update detail markdown sections */
  variant?: 'default' | 'updateDetail' | 'updateCardPreview';
  tableView?: boolean;
};

export const MarkdownText: FC<MarkdownTextProps> = ({
  components,
  tableCopy = false,
  className,
  size = 'base',
  variant = 'default',
  tableView = false,
  ...props
}) => {
  const isUpdateDetail = variant === 'updateDetail';
  const isUpdateCardPreview = variant === 'updateCardPreview';

  return (
    <div
      className={cn(
        isUpdateCardPreview
          ? 'block min-w-0 w-full text-left line-clamp-3 [&>div]:inline [&>div]:mt-0'
          : 'flex flex-col gap-1 text-left w-full max-h-fit',
        isUpdateDetail &&
          'text-sm text-neutral-800 leading-[1.44] tracking-[0.005em] [&>*:first-child]:mt-0',
        isUpdateCardPreview &&
          'text-xs text-neutral-500 leading-[1.6] tracking-[0.015em]',
        !isUpdateDetail && !isUpdateCardPreview && 'text-base text-neutral-700',
        className
      )}
      style={
        isUpdateDetail
          ? ({ letterSpacing: '0.005em' } satisfies CSSProperties)
          : isUpdateCardPreview
            ? ({ letterSpacing: '0.015em' } satisfies CSSProperties)
            : undefined
      }
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          table: ({ children }) => (
            <div className="overflow-x-auto my-4 rounded-lg border border-gray-200 shadow-sm w-fit">
              <table className="min-w-full border-collapse">{children}</table>
            </div>
          ),
          h1: ({ children }) =>
            isUpdateDetail ? (
              <div className={cn('mt-4 mb-2', UPDATE_DETAIL_BODY)}>
                {mapTextForUpdateDetail(children)}
              </div>
            ) : isUpdateCardPreview ? (
              <div
                className={cn('mt-2 first:mt-0 mb-0', UPDATE_CARD_PREVIEW_BODY)}
              >
                {children}
              </div>
            ) : (
              <Text variant="header1" className="mt-4 mb-2">
                {children}
              </Text>
            ),
          h2: ({ children }) =>
            isUpdateDetail ? (
              <div className={cn('mt-4 mb-2', UPDATE_DETAIL_BODY)}>
                {mapTextForUpdateDetail(children)}
              </div>
            ) : isUpdateCardPreview ? (
              <div
                className={cn('mt-2 first:mt-0 mb-0', UPDATE_CARD_PREVIEW_BODY)}
              >
                {children}
              </div>
            ) : (
              <Text variant="header2" className="mt-4 mb-2">
                {children}
              </Text>
            ),
          h3: ({ children }) =>
            isUpdateDetail ? (
              <div className={cn('mt-4 mb-2', UPDATE_DETAIL_BODY)}>
                {mapTextForUpdateDetail(children)}
              </div>
            ) : isUpdateCardPreview ? (
              <div
                className={cn('mt-2 first:mt-0 mb-0', UPDATE_CARD_PREVIEW_BODY)}
              >
                {children}
              </div>
            ) : (
              <Text variant="header3" className="mt-4 mb-2">
                {children}
              </Text>
            ),
          h4: ({ children }) =>
            isUpdateDetail ? (
              <div className={cn('mt-4 mb-2', UPDATE_DETAIL_BODY)}>
                {mapTextForUpdateDetail(children)}
              </div>
            ) : isUpdateCardPreview ? (
              <div
                className={cn('mt-2 first:mt-0 mb-0', UPDATE_CARD_PREVIEW_BODY)}
              >
                {children}
              </div>
            ) : (
              <Text variant="caption-bold" mt="4" mb="2" color="secondary">
                {children}
              </Text>
            ),
          thead: ({ children }) => (
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="bg-white divide-y divide-gray-200">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-gray-50 transition-colors">{children}</tr>
          ),
          th: ({ children }) => (
            <th
              className={cn(
                'px-4 py-3 text-left uppercase tracking-wider text-gray-700',
                isUpdateDetail && 'text-sm',
                isUpdateCardPreview && 'text-xs',
                !isUpdateDetail && !isUpdateCardPreview && 'text-xs',
                isUpdateDetail || isUpdateCardPreview
                  ? 'font-normal'
                  : 'font-semibold'
              )}
            >
              {children}
            </th>
          ),
          td: ({ children }) => {
            const handleCopy = () => {
              if (typeof children === 'string') {
                navigator.clipboard.writeText(children).then(() => {
                  toast('Copied to clipboard');
                });
              }
            };
            if (!tableCopy) {
              return (
                <td
                  className={cn(
                    'px-4 py-3 text-gray-700',
                    isUpdateDetail &&
                      'text-sm font-normal leading-[1.44] tracking-[0.005em]',
                    isUpdateCardPreview &&
                      'text-xs font-normal leading-[1.6] tracking-[0.015em]',
                    !isUpdateDetail && !isUpdateCardPreview && 'text-sm'
                  )}
                >
                  {children}
                </td>
              );
            }
            return (
              <td className="group px-4 py-3 text-sm text-gray-700 relative">
                <Box>{children}</Box>
                <Button
                  className={cn(
                    'w-6 h-6',
                    'opacity-0 group-hover:opacity-100',
                    'transition-opacity',
                    'absolute top-0.5 right-0.5',
                    'hover:bg-opacity-50'
                  )}
                  onClick={handleCopy}
                  size="sm"
                  variant="ghost"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </td>
            );
          },
          p: ({ children }) =>
            isUpdateDetail ? (
              <div className={cn('mt-1.5', UPDATE_DETAIL_BODY)}>
                {mapTextForUpdateDetail(children)}
              </div>
            ) : isUpdateCardPreview ? (
              <div className={cn('mt-2 first:mt-0', UPDATE_CARD_PREVIEW_BODY)}>
                {children}
              </div>
            ) : (
              <Text
                as="div"
                variant={size === 'sm' ? 'body-sm' : 'body'}
                className={cn('mt-2', tableView && 'text-black font-medium')}
              >
                {children}
              </Text>
            ),
          strong: ({ children }) =>
            isUpdateDetail ? (
              <span className={UPDATE_DETAIL_BODY}>
                {mapTextForUpdateDetail(children)}
              </span>
            ) : isUpdateCardPreview ? (
              <span className={UPDATE_CARD_PREVIEW_BODY}>{children}</span>
            ) : (
              <Text
                as="span"
                variant={size === 'sm' ? 'body-sm-bold' : 'body-bold'}
              >
                {children}
              </Text>
            ),
          ol: ({ children }) => (
            <ol
              className={cn(
                'pl-5 list-decimal list-inside',
                isUpdateDetail && UPDATE_DETAIL_BODY,
                isUpdateCardPreview && UPDATE_CARD_PREVIEW_BODY,
                tableView && 'text-black font-medium'
              )}
            >
              {children}
            </ol>
          ),
          ul: ({ children }) => (
            <ul
              className={cn(
                'pl-5 list-disc list-inside',
                isUpdateDetail && UPDATE_DETAIL_BODY,
                isUpdateCardPreview && UPDATE_CARD_PREVIEW_BODY,
                tableView && 'text-black font-medium'
              )}
            >
              {children}
            </ul>
          ),
          li: ({ children }) => (
            <li
              className={cn(
                'list-item mb-0.5 last:mb-0 [&>p]:inline [&>div]:inline',
                !isUpdateDetail &&
                  !isUpdateCardPreview &&
                  (size === 'sm' ? 'text-sm' : 'text-base'),
                isUpdateDetail && UPDATE_DETAIL_BODY,
                isUpdateCardPreview && UPDATE_CARD_PREVIEW_BODY,
                tableView && 'text-black font-medium'
              )}
            >
              {children}
            </li>
          ),
          a: ({ href, children }) => {
            if (!href) {
              return <p className="text-red-200">This link is not valid</p>;
            }
            if (href.startsWith('mailto:')) {
              const email = href.split('mailto:')[1];
              return (
                <a
                  href={href}
                  className={cn(
                    isUpdateDetail && 'text-neutral-800 underline',
                    isUpdateCardPreview && 'text-neutral-500 underline'
                  )}
                >
                  {email}
                </a>
              );
            }

            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={href}
                    rel="noreferrer"
                    target="_blank"
                    className={cn(
                      isUpdateDetail && 'text-neutral-800 underline',
                      isUpdateCardPreview && 'text-neutral-500 underline'
                    )}
                  >
                    {isUpdateDetail
                      ? mapTextForUpdateDetail(children)
                      : children}
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  This link will redirect you to the external website
                </TooltipContent>
              </Tooltip>
            );
          },
          // we don't trust images, so we don't render them
          img: ({ alt }) => <Box>{alt} - img</Box>,
          blockquote: ({ children }) =>
            isUpdateDetail ? (
              <blockquote
                className={cn(
                  'border-l-2 border-neutral-200 pl-3 my-2',
                  UPDATE_DETAIL_BODY
                )}
              >
                {mapTextForUpdateDetail(children)}
              </blockquote>
            ) : isUpdateCardPreview ? (
              <blockquote
                className={cn(
                  'border-l-2 border-neutral-200 pl-3 my-2',
                  UPDATE_CARD_PREVIEW_BODY
                )}
              >
                {children}
              </blockquote>
            ) : (
              <blockquote className="border-l-2 border-neutral-200 pl-3 my-2">
                {children}
              </blockquote>
            ),
          code: codeProps => {
            const position = codeProps.node?.position;
            const startLine = position?.start?.line;
            const endLine = position?.end?.line;
            const isInline = startLine === endLine;

            const handleCopy = () => {
              if (typeof codeProps.children === 'string') {
                navigator.clipboard.writeText(codeProps.children).then(() => {
                  toast('Copied to clipboard');
                });
              }
            };

            return isInline ? (
              <code
                className={cn(
                  'inline-block group relative',
                  isUpdateDetail && UPDATE_DETAIL_BODY,
                  isUpdateCardPreview && UPDATE_CARD_PREVIEW_BODY
                )}
              >
                {isUpdateDetail
                  ? mapTextForUpdateDetail(codeProps.children)
                  : codeProps.children}
                <Button
                  className={cn(
                    'w-6 h-6',
                    'opacity-0 group-hover:opacity-100',
                    'transition-opacity',
                    'absolute top-0.5 right-0.5',
                    'hover:bg-opacity-50'
                  )}
                  onClick={handleCopy}
                  size="sm"
                  variant="ghost"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </code>
            ) : (
              <Box
                className={cn(
                  'w-full border border-secondary px-1 py-0.5 overflow-scroll group relative',
                  isUpdateDetail && UPDATE_DETAIL_BODY,
                  isUpdateCardPreview && UPDATE_CARD_PREVIEW_BODY
                )}
              >
                <code>
                  {isUpdateDetail
                    ? mapTextForUpdateDetail(codeProps.children)
                    : codeProps.children}
                  <Button
                    className={cn(
                      'w-6 h-6',
                      'opacity-0 group-hover:opacity-100',
                      'transition-opacity',
                      'absolute top-0.5 right-0.5',
                      'hover:bg-opacity-50'
                    )}
                    onClick={handleCopy}
                    size="sm"
                    variant="ghost"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </code>
              </Box>
            );
          },
          ...components,
        }}
        {...props}
      />
    </div>
  );
};
